import { BreadcrumbComponent } from "@/components/breadcrumb/Breadcrumb";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { PersonaDetalle } from "../schema/person.schema";
import {
  associateAccountToPerson,
  AssociateAccountToPersonProps,
  getPersonaDetalle,
} from "@/services/persons.service";
import { toaster } from "@/components/ui/toaster";
import { AssociateAccountCard } from "@/components/common/section-components/AssociateAccountCard";
import { AxiosError } from "axios";
import { handlePermissionError } from "@/utils/permission-check";
import { useRoles } from "@/hooks/useRoles";

export const PersonAssociateAccountPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  const [loadingPersonData, setLoadingPersonData] = useState<boolean>(false);
  const navigate = useNavigate();

  const { id } = useParams();

  const [loadingPersons, setLoadingPersons] = useState<boolean>(false);
  const [loadingAssociateAccount, setLoadingAssociateAccount] =
    useState<boolean>(false);

  const [personData, setPersonData] = useState<PersonaDetalle | null>();

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchPersonDetail = async (id: number) => {
    setLoadingPersonData(true);
    const response = await getPersonaDetalle({ id });

    setPersonData(response);
    setLoadingPersonData(false);
  };

  useEffect(() => {
    if (isActive && id) fetchPersonDetail(Number(id));
  }, [isActive, id]);

  const methods = useForm();

  const { fetchPermissions } = useRoles();

  const handleCancel = () => {
    navigate(AppRoutes.PERSON_DETAIL.replace(":id", String(id)));
  };

  const handleAssociateAccount = methods.handleSubmit(
    async (event: FieldValues) => {
      setLoadingAssociateAccount(true);
      try {
        const objToSend: AssociateAccountToPersonProps = {
          idPersona: Number(personData?.id),
          cuenta: event?.cuenta?.value,
        };

        await associateAccountToPerson(objToSend);

        toaster.create({
          type: "success",
          title: "Cuenta asociada con éxito.",
        });

        navigate(
          AppRoutes.PERSON_DETAIL.replace(":id", String(personData?.id)),
          {
            state: {
              tab: "cuentas_asociadas",
            },
          },
        );
      } catch (error) {
        const errorObj = error as AxiosError<{ message: string }>;
        const handled = await handlePermissionError(
          error,
          navigate,
          fetchPermissions,
          AppRoutes.PERSON_DETAIL.replace(":id", String(personData?.id)),
          {
            tab: "cuentas_asociadas",
          },
        );
        if (!handled) {
          toaster.create({
            type: "error",
            title: errorObj?.response?.data?.message ?? "No se pudo asociar la cuenta.",
          });
        } 
      
        throw error;
      } finally {
        setLoadingAssociateAccount(false);
      }
    },
  );

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Asociar cuenta"
        addButtonLabel="Asociar"
        cancelButtonLabel="Cancelar"
        loading={loadingPersonData || loadingAssociateAccount}
        breadcrumbComponent={
          <BreadcrumbComponent
            options={[
              {
                label: "Maestro de personas",
                href: AppRoutes.PERSONS,
              },
              {
                label: personData
                  ? personData?.nombre + " " + personData?.apellido
                  : "Cargando...",
                href: AppRoutes.PERSON_DETAIL.replace(
                  ":id",
                  String(personData?.id),
                ),
              },
              {
                label: "Asociar cuenta",
                href: "#",
                strong: true,
              },
            ]}
          />
        }
        handleCancel={handleCancel}
        handleAdd={handleAssociateAccount}
      />

      {!loadingPersonData && (
        <FormProvider {...methods}>
          <AssociateAccountCard
            loadingPersons={loadingPersons}
            setLoadingPersons={setLoadingPersons}
          />
        </FormProvider>
      )}
    </Flex>
  );
};
